import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TenantAdminLayout from './Layout/TenantAdminLayout';
import { useTenant } from './Layout/TenantContext';
import SubscriptionGuard from '../components/SubscriptionGuard';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { 
  FiCalendar, 
  FiUsers, 
  FiLayers, 
  FiBook, 
  FiGlobe, 
  FiUserPlus, 
  FiSearch, 
  FiClipboard,
  FiMessageSquare,
  FiClock,
  FiCreditCard,
  FiHardDrive,
  FiFileText,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiX,
  FiCheck,
  FiInfo
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  getTenantPendingCount, 
  getTenantDocuments, 
  acceptTenantDocument, 
  acceptAllTenantDocuments 
} from '../api';

const Admin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantData, fetchTenantData, loading } = useTenant();
  
  const userId = location.state?.userId || localStorage.getItem('userId');
  const tenantId = location.state?.tenantId || localStorage.getItem('tenantId');

  const [searchTerm, setSearchTerm] = useState('');
  const [dataFetched, setDataFetched] = useState(false);

  // Legal Documents State
  const [legalPendingCount, setLegalPendingCount] = useState(0);
  const [legalHasPending, setLegalHasPending] = useState(false);
  const [legalHasDeadlinePassed, setLegalHasDeadlinePassed] = useState(false);
  const [, setLegalLoading] = useState(true);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalDocuments, setLegalDocuments] = useState([]);
  const [acceptingAll, setAcceptingAll] = useState(false);
  const [acceptingDoc, setAcceptingDoc] = useState(null);

  // Modal state for custom notifications
  const [modal, setModal] = useState({
    isOpen: false,
    type: '', // 'success', 'error', 'info', 'warning'
    title: '',
    message: '',
    onConfirm: null,
    confirmText: '',
    cancelText: ''
  });

  // ✅ Get user role
  const userRole = localStorage.getItem('userRole') || 'tenant_admin';

  // ✅ Use the module access hook - fetches immediately when component mounts
  const { hasModuleAccess, loading: modulesLoading, hasActivePlan, planName, isExpiringSoon, daysRemaining } = useModuleAccess(tenantId);

  // ✅ Check if website management is accessible
  const hasWebsiteAccess = useMemo(() => {
    return hasModuleAccess('websiteManagement');
  }, [hasModuleAccess]);

  // ✅ Only show warning if expiring within 7 days AND days remaining > 0
  const showExpiryWarning = isExpiringSoon && daysRemaining > 0 && daysRemaining <= 7;

  // Helper Functions
  const getDocumentTypeLabel = (type) => {
    const labels = {
      terms_of_service: 'Terms of Service',
      privacy_policy: 'Privacy Policy',
      refund_policy: 'Refund Policy',
      cookie_policy: 'Cookie Policy'
    };
    return labels[type] || type;
  };

  const getDocumentIcon = (type) => {
    const icons = {
      terms_of_service: '📋',
      privacy_policy: '🔒',
      refund_policy: '💰',
      cookie_policy: '🍪'
    };
    return icons[type] || '📄';
  };

  const formatDate = (date) => {
    if (!date) return 'Not specified';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'Not specified';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Modal helper functions
  const showSuccessModal = (title, message) => {
    setModal({
      isOpen: true,
      type: 'success',
      title,
      message,
      onConfirm: null,
      confirmText: 'OK',
      cancelText: ''
    });
  };

  const showErrorModal = (title, message) => {
    setModal({
      isOpen: true,
      type: 'error',
      title,
      message,
      onConfirm: null,
      confirmText: 'OK',
      cancelText: ''
    });
  };

  const showConfirmModal = (title, message, onConfirm, confirmText = 'Confirm', cancelText = 'Cancel') => {
    setModal({
      isOpen: true,
      type: 'warning',
      title,
      message,
      onConfirm,
      confirmText,
      cancelText
    });
  };

  const closeModal = () => {
    setModal((prevModal) => ({ ...prevModal, isOpen: false }));
  };

  const handleModalConfirm = () => {
    if (modal.onConfirm) {
      modal.onConfirm();
    }
    closeModal();
  };

  // Fetch data only once when component mounts
  useEffect(() => {
    if (userId && tenantId && !dataFetched) {
      fetchTenantData(userId, tenantId).finally(() => {
        setDataFetched(true);
      });
    }
  }, [userId, tenantId, fetchTenantData, dataFetched]);

  const fetchAllDocuments = useCallback(async () => {
    try {
      const response = await getTenantDocuments();
      setLegalDocuments(response.data.documents);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }, []);

  const fetchLegalPendingCount = useCallback(async () => {
    setLegalLoading(true);
    try {
      const response = await getTenantPendingCount();
      setLegalPendingCount(response.data.pendingCount);
      setLegalHasPending(response.data.hasPending);
      setLegalHasDeadlinePassed(response.data.hasDeadlinePassed || false);
      
      // If there are pending documents, fetch all documents and show modal
      if (response.data.hasPending) {
        await fetchAllDocuments();
        setShowLegalModal(true);
      }
    } catch (error) {
      console.error('Error fetching legal pending count:', error);
    } finally {
      setLegalLoading(false);
    }
  }, [fetchAllDocuments]);

  // Fetch legal documents pending count
  useEffect(() => {
    if (tenantId) {
      fetchLegalPendingCount();
    }
  }, [tenantId, fetchLegalPendingCount]);

  const canCloseModal = () => {
    const stillPending = legalDocuments.filter(doc => !doc.accepted);
    if (stillPending.length === 0) return true;
    
    const deadlinePassed = stillPending.some(doc => doc.isDeadlinePassed);
    return !deadlinePassed;
  };

  const handleAcceptDocument = async (documentId) => {
    setAcceptingDoc(documentId);
    try {
      await acceptTenantDocument(documentId);
      await fetchAllDocuments();
      // Check if all documents are accepted
      const updatedDocs = legalDocuments.map(doc => 
        doc._id === documentId ? { ...doc, accepted: true } : doc
      );
      setLegalDocuments(updatedDocs);
      const stillPending = updatedDocs.filter(doc => !doc.accepted);
      if (stillPending.length === 0) {
        setShowLegalModal(false);
        setLegalHasPending(false);
        setLegalPendingCount(0);
        showSuccessModal('All Accepted', 'All policies have been accepted successfully!');
      } else {
        setLegalPendingCount(stillPending.length);
        showSuccessModal('Accepted', 'Document accepted successfully!');
      }
    } catch (error) {
      console.error('Error accepting document:', error);
      showErrorModal('Error', error.response?.data?.message || 'Failed to accept document. Please try again.');
    } finally {
      setAcceptingDoc(null);
    }
  };

  const handleAcceptAll = async () => {
    showConfirmModal(
      'Accept All Policies',
      `You are about to accept all ${legalDocuments.filter(d => !d.accepted).length} pending policies.\n\nThis action cannot be undone.`,
      async () => {
        setAcceptingAll(true);
        try {
          await acceptAllTenantDocuments();
          await fetchAllDocuments();
          setShowLegalModal(false);
          setLegalHasPending(false);
          setLegalPendingCount(0);
          showSuccessModal('All Accepted', 'All policies have been accepted successfully!');
        } catch (error) {
          console.error('Error accepting all documents:', error);
          showErrorModal('Error', error.response?.data?.message || 'Failed to accept all documents. Please try again.');
        } finally {
          setAcceptingAll(false);
        }
      },
      'Accept All',
      'Cancel'
    );
  };

  const handleCloseModal = () => {
    const stillPending = legalDocuments.filter(doc => !doc.accepted);
    
    if (stillPending.length === 0) {
      setShowLegalModal(false);
      return;
    }

    // Check if any pending document has deadline passed
    const deadlinePassed = stillPending.some(doc => doc.isDeadlinePassed);
    
    if (deadlinePassed) {
      // Force modal to stay open - show error modal
      showErrorModal(
        'Action Required',
        '⚠️ The acceptance deadline has passed for some documents.\n\nYou must accept all pending documents to continue using the system.\n\nPlease accept the documents below to proceed.'
      );
      return;
    }

    // If within deadline, allow closing with confirmation
    showConfirmModal(
      'Pending Acceptance',
      `You have ${stillPending.length} document${stillPending.length > 1 ? 's' : ''} pending acceptance.\n\nYou can close and accept them later, but you must accept them before the deadline.\n\nDo you want to continue?`,
      () => {
        setShowLegalModal(false);
      },
      'Yes, Close',
      'Stay Here'
    );
  };

  const navigateToSubscription = useCallback(() => {
    const finalTenantId = tenantId || localStorage.getItem('tenantId');
    const finalUserId = userId || localStorage.getItem('userId');
    
    navigate('/tenant-admin/subscription', { 
      state: { 
        userId: finalUserId, 
        tenantId: finalTenantId,
        fromDashboard: true
      } 
    });
  }, [navigate, userId, tenantId]);

  const navigateToStorage = useCallback(() => {
    const finalTenantId = tenantId || localStorage.getItem('tenantId');
    const finalUserId = userId || localStorage.getItem('userId');
    
    navigate('/tenant-admin/storage', { 
      state: { 
        userId: finalUserId, 
        tenantId: finalTenantId
      } 
    });
  }, [navigate, userId, tenantId]);

  const navigateToLegalDocuments = useCallback(() => {
    navigate('/tenant-admin/legal-documents', { 
      state: { 
        userId, 
        tenantId 
      } 
    });
  }, [navigate, userId, tenantId]);

  const handleOrganizationAction = useCallback(() => {
    navigate(tenantData.organizationExists 
      ? `/tenant-admin/manage-organization/${tenantId}`
      : `/tenant-admin/create-organization/${tenantId}`,
      { state: { userId, tenantId } }
    );
  }, [navigate, tenantData.organizationExists, tenantId, userId]);

  const handleCommunicationAction = useCallback(() => {
    const finalUserId = userId || localStorage.getItem('userId');
    const finalTenantId = tenantId || localStorage.getItem('tenantId');
    
    navigate('/tenant-admin/communication', { 
      state: { 
        userId: finalUserId, 
        tenantId: finalTenantId, 
        token: localStorage.getItem('token'),
        userDetails: tenantData.userDetails,
        organizationExists: tenantData.organizationExists 
      } 
    });
  }, [navigate, userId, tenantId, tenantData.userDetails, tenantData.organizationExists]);

  // Memoize card icons
  const cardIcons = useMemo(() => ({
    'Manage Organization': <FiUsers className="h-6 w-6 text-white" />,
    'Create Organization': <FiUsers className="h-6 w-6 text-white" />,
    'Manage Academic Years': <FiCalendar className="h-6 w-6 text-white" />,
    'Manage Staff': <FiUserPlus className="h-6 w-6 text-white" />,
    'Manage Departments': <FiLayers className="h-6 w-6 text-white" />,
    'Manage Classes': <FiBook className="h-6 w-6 text-white" />,
    'Website Manager': <FiGlobe className="h-6 w-6 text-white" />,
    'Manage Students': <FiUsers className="h-6 w-6 text-white" />,
    'Subject Attendance Rules': <FiClipboard className="h-6 w-6 text-white" />,
    'Manage Programs': <FiLayers className="h-6 w-6 text-white" />,
    'Manage Timetable': <FiCalendar className="h-6 w-6 text-white" />,
    'Attendance Management': <FiClipboard className="h-6 w-6 text-white" />,
    'Communication Panel': <FiMessageSquare className="h-6 w-6 text-white" />,
    'Storage Management': <FiHardDrive className="h-6 w-6 text-white" />,
    'Subscription Management': <FiCreditCard className="h-6 w-6 text-white" />,
    'Policies & Agreements': <FiFileText className="h-6 w-6 text-white" />
  }), []);

  // Dashboard cards
  const dashboardCards = useMemo(() => {
    const cards = [
      {
        title: tenantData.organizationExists ? 'Manage Organization' : 'Create Organization',
        description: tenantData.organizationExists
          ? 'Manage your organization details and settings'
          : 'Set up your organization profile',
        action: handleOrganizationAction,
        color: 'bg-indigo-500',
        locked: false
      },
      {
        title: 'Manage Academic Years',
        description: 'Configure academic years and terms',
        path: `/tenant-admin/academic-components/academic-year/${tenantId}`,
        color: 'bg-blue-500',
        locked: false
      },
      {
        title: 'Manage Staff',
        description: 'Add and manage staff members',
        path: `/tenant-admin/manage-staff/${tenantId}`,
        color: 'bg-green-500',
        locked: false
      },
      {
        title: 'Manage Departments',
        description: 'Create and organize departments',
        action: () => {
          navigate('/tenant-admin/department', { state: { userId, tenantId } });
        },
        color: 'bg-purple-500',
        locked: false
      },
      {
        title: 'Manage Classes',
        description: 'Create and organize classes',
        action: () => {
          navigate('/tenant-admin/class-manager', { state: { tenantId, userId } });
        },
        color: 'bg-yellow-500',
        locked: false
      },
      {
        title: 'Manage Students',
        description: 'Add and manage student records',
        path: `/tenant-admin/manage-students`,
        color: 'bg-red-500',
        locked: false
      },
      {
        title: 'Manage Timetable',
        description: 'Create and manage class timetables',
        action: () => {
          navigate('/tenant-admin/timetable', { state: { userId, tenantId } });
        },
        color: 'bg-cyan-500',
        locked: false
      },
      {
        title: 'Subject Attendance Rules',
        description: 'Create and manage attendance rules per subject',
        action: () => {
          navigate('/tenant-admin/subject-attendance-rules', { state: { userId, tenantId } });
        },
        color: 'bg-indigo-600',
        locked: false
      },
      {
        title: 'Attendance Management',
        description: 'View, edit, block/unblock, and delete attendance sessions',
        action: () => {
          navigate('/tenant-admin/attendance', {
            state: { userId, tenantId, userDetails: tenantData.userDetails, organizationExists: tenantData.organizationExists }
          });
        },
        color: 'bg-orange-500',
        locked: false
      },
      {
        title: 'Manage Programs',
        description: 'Create and manage academic programs',
        action: () => {
          navigate('/tenant-admin/programs', { state: { userId, tenantId } });
        },
        color: 'bg-teal-500',
        locked: false
      },
      {
        title: 'Communication Panel',
        description: 'Manage email/SMS credits, bundles, and communication settings',
        action: handleCommunicationAction,
        color: 'bg-emerald-500',
        locked: false
      }
    ];

    // ✅ Website Manager - Only show if module is accessible
    if (hasWebsiteAccess) {
      cards.push({
        title: 'Website Manager',
        description: 'Customize your organization website',
        action: () => {
          navigate('/tenant-admin/website-manager', { state: { userId, tenantId } });
        },
        color: 'bg-pink-500',
        locked: false
      });
    }

    // ✅ Policies & Agreements Card
    cards.push({
      title: 'Policies & Agreements',
      description: legalHasPending 
        ? `${legalPendingCount} policy${legalPendingCount > 1 ? 's' : ''} pending review`
        : 'All policies have been accepted',
      action: navigateToLegalDocuments,
      color: legalHasPending ? 'bg-amber-500' : 'bg-green-500',
      locked: false,
      badge: legalHasPending ? legalPendingCount : null,
      badgeColor: legalHasPending ? 'bg-amber-600' : 'bg-green-600'
    });

    // Storage Management
    cards.push({
      title: 'Storage Management',
      description: 'View, upload, and manage all files in your tenant storage',
      action: navigateToStorage,
      color: 'bg-violet-500',
      locked: false
    });

    // ✅ Subscription Management - ALWAYS LAST
    cards.push({
      title: 'Subscription Management',
      description: hasActivePlan 
        ? `Current Plan: ${planName}`
        : 'Manage your subscription plan and billing',
      action: navigateToSubscription,
      color: 'bg-indigo-600',
      locked: false
    });

    return cards;
  }, [
    tenantData.organizationExists,
    tenantData.userDetails,
    tenantId,
    userId,
    handleOrganizationAction,
    handleCommunicationAction,
    navigate,
    hasActivePlan,
    planName,
    navigateToSubscription,
    navigateToStorage,
    hasWebsiteAccess,
    navigateToLegalDocuments,
    legalHasPending,
    legalPendingCount
  ]);

  // Filter cards by search term
  const filteredCards = useMemo(() => {
    return dashboardCards.filter(card =>
      card.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [dashboardCards, searchTerm]);

  // Modal Component
  const ModalComponent = () => {
    const getModalStyles = () => {
      switch(modal.type) {
        case 'success':
          return {
            icon: <FiCheckCircle className="w-12 h-12 text-green-500" />,
            bg: 'bg-green-50',
            border: 'border-green-200',
            button: 'bg-green-600 hover:bg-green-700'
          };
        case 'error':
          return {
            icon: <FiAlertCircle className="w-12 h-12 text-red-500" />,
            bg: 'bg-red-50',
            border: 'border-red-200',
            button: 'bg-red-600 hover:bg-red-700'
          };
        case 'warning':
          return {
            icon: <FiAlertCircle className="w-12 h-12 text-amber-500" />,
            bg: 'bg-amber-50',
            border: 'border-amber-200',
            button: 'bg-amber-600 hover:bg-amber-700'
          };
        default:
          return {
            icon: <FiInfo className="w-12 h-12 text-blue-500" />,
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            button: 'bg-blue-600 hover:bg-blue-700'
          };
      }
    };

    const styles = getModalStyles();

    return (
      <AnimatePresence>
        {modal.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className={`bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden ${styles.bg} border ${styles.border}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="flex justify-center mb-4">
                  {styles.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {modal.title}
                </h3>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {modal.message}
                </p>
              </div>
              <div className={`px-6 py-4 ${styles.bg} border-t ${styles.border} flex flex-col sm:flex-row gap-3 justify-center`}>
                {modal.cancelText && (
                  <button
                    onClick={closeModal}
                    className="px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 rounded-xl transition-colors text-sm font-medium border border-gray-200 w-full sm:w-auto"
                  >
                    {modal.cancelText}
                  </button>
                )}
                <button
                  onClick={handleModalConfirm}
                  className={`px-6 py-2.5 text-white rounded-xl transition-colors text-sm font-medium w-full sm:w-auto ${styles.button}`}
                >
                  {modal.confirmText || 'OK'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  // ✅ Show loading state while modules are being fetched
  if (modulesLoading) {
    return (
      <TenantAdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading module access...</p>
          </div>
        </div>
      </TenantAdminLayout>
    );
  }

  // Loading states for tenant data
  if ((loading && !dataFetched)) {
    return (
      <TenantAdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-40"></div>
              ))}
            </div>
          </div>
        </div>
      </TenantAdminLayout>
    );
  }

  return (
    <SubscriptionGuard 
      subscription={{ hasActivePlan, planName, isExpiringSoon, daysRemaining }}
      isLoading={false}
      showWarning={true}
      userRole={userRole}
    >
      <TenantAdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* ✅ Only show expiry warning when expiring within 7 days */}
          {showExpiryWarning && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-3"
            >
              <FiClock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  ⚠️ Your subscription will expire in <span className="font-bold">{daysRemaining}</span> days.
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Please contact your administrator to renew your subscription.
                </p>
              </div>
              <button
                onClick={navigateToSubscription}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
              >
                Manage Subscription
              </button>
            </motion.div>
          )}

          {/* Welcome Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8"
          >
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="mt-2 text-gray-600">
                Welcome back, <span className="font-semibold text-indigo-600">{tenantData.profileName || 'Admin'}</span>
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 min-w-[250px]">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search features..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-center text-sm text-gray-500 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
                <FiCalendar className="flex-shrink-0 mr-2 h-5 w-5 text-gray-400" />
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>
          </motion.div>

          {/* Dashboard Cards */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              {searchTerm ? (
                <>
                  {filteredCards.length > 0 ? (
                    `Showing ${filteredCards.length} feature${filteredCards.length !== 1 ? 's' : ''}`
                  ) : (
                    'No matching features found'
                  )}
                </>
              ) : (
                'Quick Access'
              )}
            </h2>
            
            <AnimatePresence mode="wait">
              {filteredCards.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-gray-50 rounded-xl p-8 text-center"
                >
                  <p className="text-gray-500">No features match your search</p>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    Clear search
                  </button>
                </motion.div>
              ) : (
                <motion.div 
                  key="cards"
                  layout
                  className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                >
                  {filteredCards.map((card, index) => (
                    <motion.div
                      key={card.title}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      whileHover={!card.locked ? { y: -5 } : {}}
                      className={`bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 transition-all duration-200 relative ${
                        card.locked 
                          ? 'opacity-60 cursor-not-allowed' 
                          : 'hover:shadow-md cursor-pointer'
                      }`}
                      onClick={() => {
                        if (!card.locked) {
                          if (card.action) {
                            card.action();
                          } else if (card.path) {
                            navigate(card.path, { state: { userId, tenantId } });
                          }
                        }
                      }}
                    >
                      {/* Badge for Policies & Agreements with pending count */}
                      {card.badge && (
                        <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium text-white ${card.badgeColor}`}>
                          {card.badge} pending
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-start">
                          <div className={`flex-shrink-0 rounded-lg p-3 ${card.color}`}>
                            {cardIcons[card.title] || <FiCalendar className="h-6 w-6 text-white" />}
                          </div>
                          <div className="ml-4 flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">{card.title}</h3>
                            <p className="mt-1 text-sm text-gray-500">{card.description}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Recent Activity Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-12"
          >
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-6">
                <p className="text-gray-500 text-center py-8">
                  Activity feed coming soon
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </TenantAdminLayout>

      {/* ============================================================ */}
      {/* LEGAL DOCUMENTS ACCEPTANCE MODAL - Professional UI */}
      {/* ============================================================ */}
      <AnimatePresence>
        {showLegalModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-indigo-700 to-purple-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg flex-shrink-0">
                    <FiFileText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-lg font-semibold text-white truncate">Policies & Agreements</h2>
                    <p className="text-xs text-white/80 truncate">
                      Please review and accept the following policies to continue
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                  {legalHasDeadlinePassed && (
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-500/30 rounded-lg text-white text-xs sm:text-sm font-medium flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden xs:inline">Deadline Passed</span>
                    </span>
                  )}
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white/20 rounded-lg text-white text-xs sm:text-sm font-medium">
                    {legalDocuments.filter(d => !d.accepted).length} pending
                  </span>
                  <button
                    onClick={handleCloseModal}
                    className={`p-1.5 sm:p-2 hover:bg-white/20 rounded-xl transition-colors ${
                      canCloseModal() 
                        ? 'text-white/80 hover:text-white cursor-pointer' 
                        : 'text-white/40 cursor-not-allowed'
                    }`}
                    disabled={!canCloseModal()}
                  >
                    <FiX className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(92vh-160px)]">
                <div className="space-y-4 sm:space-y-6">
                  {legalDocuments.map((doc) => (
                    <motion.div
                      key={doc._id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`p-4 sm:p-6 rounded-xl border-2 transition-all ${
                        doc.accepted 
                          ? 'border-green-200 bg-green-50/50' 
                          : doc.isDeadlinePassed
                            ? 'border-red-200 bg-red-50/50'
                            : 'border-amber-200 bg-amber-50/50'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                        <div className="text-2xl sm:text-3xl flex-shrink-0">
                          {getDocumentIcon(doc.documentType)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900">{doc.title}</h3>
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-700">
                              v{doc.version}
                            </span>
                            {doc.accepted ? (
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-200 text-green-700 flex items-center gap-1">
                                <FiCheckCircle className="w-3 h-3" /> Accepted
                              </span>
                            ) : doc.isDeadlinePassed ? (
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-200 text-red-700 flex items-center gap-1">
                                <FiAlertCircle className="w-3 h-3" /> Deadline Passed
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-200 text-amber-700 flex items-center gap-1">
                                <FiClock className="w-3 h-3" /> Pending Review
                              </span>
                            )}
                          </div>
                          
                          <div className="mt-1.5 flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <span className="font-medium">Type:</span> {getDocumentTypeLabel(doc.documentType)}
                            </span>
                            {doc.effectiveDate && (
                              <span className="flex items-center gap-1">
                                <FiCalendar className="w-3 h-3" />
                                Effective: {formatDate(doc.effectiveDate)}
                              </span>
                            )}
                            {doc.acceptanceEndDate && !doc.accepted && (
                              <span className={`flex items-center gap-1 ${
                                doc.isDeadlinePassed ? 'text-red-600 font-medium' : 'text-amber-600'
                              }`}>
                                <FiClock className="w-3 h-3" />
                                {doc.isDeadlinePassed ? 'Deadline passed' : `Accept by: ${formatDate(doc.acceptanceEndDate)}`}
                              </span>
                            )}
                          </div>

                          {doc.changeLog && (
                            <div className="mt-2 p-2 sm:p-3 bg-white rounded-lg border border-gray-200 text-xs sm:text-sm">
                              <p className="text-gray-600">
                                <span className="font-medium text-gray-700">What changed:</span> {doc.changeLog}
                              </p>
                            </div>
                          )}

                          {!doc.accepted && (
                            <div className="mt-3 sm:mt-4">
                              <div 
                                className="text-xs sm:text-sm text-gray-700 bg-white p-3 sm:p-4 rounded-lg border border-gray-200 max-h-[150px] sm:max-h-[200px] overflow-y-auto prose prose-sm"
                                dangerouslySetInnerHTML={{ __html: doc.content }}
                              />
                            </div>
                          )}

                          {doc.accepted && doc.acceptedAt && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-green-600">
                              <FiCheckCircle className="w-3 h-3" />
                              Accepted on {formatDateTime(doc.acceptedAt)}
                            </div>
                          )}

                          {doc.isDeadlinePassed && !doc.accepted && (
                            <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-red-600 bg-red-50 p-2 sm:p-3 rounded-lg border border-red-200">
                              <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                              <span>⚠️ The acceptance deadline has passed. You must accept this document to continue.</span>
                            </div>
                          )}
                        </div>
                        
                        {!doc.accepted && (
                          <button
                            onClick={() => handleAcceptDocument(doc._id)}
                            disabled={acceptingDoc === doc._id}
                            className={`flex-shrink-0 px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium shadow-sm ${
                              doc.isDeadlinePassed
                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                            }`}
                          >
                            {acceptingDoc === doc._id ? (
                              <>
                                <FiLoader className="w-4 h-4 animate-spin" />
                                <span className="hidden xs:inline">Accepting...</span>
                              </>
                            ) : (
                              <>
                                <FiCheck className="w-4 h-4" />
                                Accept
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">
                    {legalDocuments.filter(d => !d.accepted).length} pending
                  </span>
                  <span className="text-gray-300">|</span>
                  <span>
                    {legalDocuments.filter(d => d.accepted).length} accepted
                  </span>
                  {legalHasDeadlinePassed && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span className="text-red-600 font-medium flex items-center gap-1">
                        <FiAlertCircle className="w-3 h-3" />
                        Deadline Passed
                      </span>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  {legalDocuments.filter(d => !d.accepted).length > 0 && (
                    <button
                      onClick={handleAcceptAll}
                      disabled={acceptingAll}
                      className="flex-1 sm:flex-none px-4 sm:px-5 py-2 sm:py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium shadow-sm"
                    >
                      {acceptingAll ? (
                        <>
                          <FiLoader className="w-4 h-4 animate-spin" />
                          <span className="hidden xs:inline">Accepting All...</span>
                          <span className="xs:hidden">Accepting...</span>
                        </>
                      ) : (
                        <>
                          <FiCheckCircle className="w-4 h-4" />
                          <span className="hidden xs:inline">Accept All Policies</span>
                          <span className="xs:hidden">Accept All</span>
                        </>
                      )}
                    </button>
                  )}
                  {legalDocuments.filter(d => !d.accepted).length === 0 && (
                    <button
                      onClick={handleCloseModal}
                      className="flex-1 sm:flex-none px-4 sm:px-5 py-2 sm:py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm font-medium shadow-sm"
                    >
                      Continue to Dashboard
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Modal Component */}
      <ModalComponent />
    </SubscriptionGuard>
  );
};

export default Admin;